import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { reportClientError } from '../lib/reportError';

interface Props { children: ReactNode; }
interface State { error: Error | null; }

/**
 * Catches render-time exceptions anywhere under <Layout> and shows a
 * friendly fallback with a "Create a support ticket" link pre-filled with
 * the stack so the user can recover instead of seeing a blank screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
    // A3/A5 — auto-report so it becomes a deduped support ticket linked to the
    // user, without requiring them to click "Create a support ticket".
    void reportClientError({
      message: error?.message ?? 'render error',
      stack: error?.stack,
      componentStack: info?.componentStack ?? undefined
    });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const subject = `Unhandled error on dashboard: ${this.state.error.message.slice(0, 80)}`;
    const body = [
      'Hi support team,',
      '',
      'I hit an unhandled error on the dashboard. The page crashed.',
      '',
      `> ${this.state.error.message}`,
      '',
      `Page: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
      `Time (browser): ${new Date().toISOString()}`,
      `User agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
      '',
      'Stack:',
      '```',
      (this.state.error.stack || '').slice(0, 2000),
      '```',
    ].join('\n');
    const envelope = btoa(unescape(encodeURIComponent(JSON.stringify({ subject, body, category: 'bug' }))));

    return (
      <div role="alert" className="mx-auto my-12 max-w-xl rounded-lg border border-red-300 bg-red-50 p-6 text-sm">
        <h2 className="text-base font-semibold text-red-900">Something went wrong on this page</h2>
        <p className="mt-2 text-red-800">
          The page crashed unexpectedly. You can reload, or open a support ticket — we'll get on it.
        </p>
        <p className="mt-3 font-mono text-[11px] text-red-700/80 break-words">{this.state.error.message}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Reload page
          </button>
          <Link
            to={`/support?prefill=${encodeURIComponent(envelope)}`}
            onClick={() => this.setState({ error: null })}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
          >
            Create a support ticket
          </Link>
        </div>
      </div>
    );
  }
}
